/**
 * Mission Architecture Alignment (Sprint 2026-06-03, rebuilt 2026-06-05 PM-7).
 *
 * Renders Row 1 of the canonical Brand Studio breadcrumb stack:
 *
 *   Row 1 → BRAND STUDIO: PILLARS
 *           Foundation → Protect → Strengthen → Brand Health → Steward
 *
 * Rows 2 and 3 are owned by FlowBreadcrumb (mission row) and
 * BrandStudioBreadcrumb (text path crumb) respectively. Each component
 * gates itself; together they produce the 3-row stack paco's
 * 2026-06-05 reference screenshots specify.
 *
 * --- Sprint 2026-06-11 PM-8 Phase 1 (paco) ---
 * On the Phase-1 canonical paths (see PHASE1_CANONICAL_PATHS below) this
 * component switches to the new 2-layer canonical view:
 *
 *   Row 1 → MISSION: BUILD & PROTECT THE BRAND
 *           Foundation → Protect → Strengthen → Brand Health → Steward
 *
 *   Row 2 → <active pillar label>
 *           <sub-page chips from BRAND_PILLAR_WORKFLOWS[pillar].items>
 *
 * The FlowBreadcrumb component self-suppresses on these paths so the
 * (wrong-mission) "Grow the Brand" row is no longer rendered.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Layers, Shield, Sparkles, Activity, Compass } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  BUILD_AND_PROTECT_MISSION,
  BRAND_PILLAR_WORKFLOWS,
  detectBrandPillar,
  detectBrandStage,
  isBrandStudioPath,
} from "../../config/missionRegistry";
import BrandStageDrawer from "./BrandStageDrawer";
import { recordBrandLastVisited } from "../brand-studio/BrandStudioResumeChip";

// Sprint 2026-06-11 PM-9 Phase 1D (paco) — Lucide icon resolver.
// Stage registry references icons by name (string) so the config stays
// pure data — we resolve to the React component here.
const ICON_BY_NAME = {
  Layers, Shield, Sparkles, Activity, Compass,
};

// Sprint 2026-06-11 PM-9 Phase 2 (paco) — canonical 2-row mission
// breadcrumb is now active on EVERY Brand-section leaf page. The list
// covers all 16 routes the Brand Studio exposes (App.js authoritative).
// Future studios will mirror this pattern (see
// memory/COMPASS/01_technical/06_uxui/canonical-breadcrumb.md).
const PHASE1_CANONICAL_PATHS = [
  // Foundation (5)
  "/brand-studio/foundation/setup",
  "/brand-studio/foundation/identity",
  "/brand-studio/foundation/voice",
  "/brand-studio/foundation/repository",
  "/brand-studio/foundation/launch",
  // Protect (4)
  "/brand-studio/protect/identity-analysis",
  "/brand-studio/protect/domain-portfolio",
  "/brand-studio/protect/trademark-readiness",
  "/brand-studio/protect/evidence-center",
  // Strengthen (4)
  "/brand-studio/strengthen/diagnostic",
  "/brand-studio/strengthen/positioning",
  "/brand-studio/strengthen/audience",
  "/brand-studio/strengthen/competitor",
  // Brand Health (2)
  "/brand-studio/brand-health",
  "/brand-studio/brand-performance",
  // Steward (1)
  "/brand-studio/steward/living-bi",
];

// Sprint 2026-06-11 PM-9 Phase 1B — pillars / children that have a drawer.
// Brand Health is omitted (it's a score, not a list).
const DRAWER_PILLARS = new Set(["protect", "strengthen", "steward"]);
const DRAWER_CHILDREN = new Set(["identity", "repository", "launch"]);

function localized(obj, lang) {
  if (!obj) return "";
  return obj[lang] || obj.en || "";
}

/**
 * Sprint 2026-06-11 PM-9 Phase 1C — pulse heartbeat dot.
 *
 * Renders a tiny animated dot next to a label / badge when the underlying
 * section had activity in the last 24h. Visually surfaces "where the
 * action is today" without the user clicking around.
 *
 *   <PulseDot color="#6366F1" testid="pulse-foundation" />
 */
function PulseDot({ color, testid }) {
  return (
    <span
      className="relative inline-flex w-2 h-2 ml-1 shrink-0"
      data-testid={testid}
      aria-hidden="true"
    >
      <span
        className="absolute inset-0 rounded-full opacity-60 animate-ping"
        style={{ backgroundColor: color }}
      />
      <span
        className="relative inline-flex rounded-full w-2 h-2"
        style={{ backgroundColor: color }}
      />
    </span>
  );
}

export { isBrandStudioPath };

export default function BrandStudioPillarsBreadcrumb() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  // Sprint 2026-06-11 PM-9 Phase 1B — fetch count map for badges.
  // Sprint 2026-06-11 PM-9 Phase 1C — also includes recent_24h flags for
  // the pulse-heartbeat indicator.
  const [counts, setCounts] = useState({ pillars: {}, children: {}, recent_24h: { pillars: {}, children: {} } });
  const [openDrawer, setOpenDrawer] = useState(null); // "<scope>:<id>" or null
  const badgeRefs = useRef({});
  useEffect(() => {
    let cancelled = false;
    api.get("/brand-studio/mission-counts")
      .then((r) => {
        if (cancelled || !r?.data) return;
        setCounts({
          pillars: r.data.pillars || {},
          children: r.data.children || {},
          recent_24h: r.data.recent_24h || { pillars: {}, children: {} },
        });
      })
      .catch(() => { /* badges simply won't render if counts fail */ });
    return () => { cancelled = true; };
  }, [pathname]);

  // Sprint 2026-06-11 PM-9 Phase 1D — record last-visited leaf so the
  // Resume chip on the Brand Studio landing page can deeplink back.
  // We record on every path-change that resolves to a known pillar AND
  // a known sub-page id (avoids polluting storage on transient routes).
  useEffect(() => {
    const pillar = detectBrandPillar(pathname);
    if (!pillar) return;
    const workflow = BRAND_PILLAR_WORKFLOWS[pillar];
    if (!workflow) return;
    const child = workflow.items.find((c) => c.page === pathname);
    if (!child) return;
    recordBrandLastVisited(pathname, pillar, child.id);
  }, [pathname]);

  if (!isBrandStudioPath(pathname)) return null;
  // On the canonical home (/brand-studio or /brand-studio/home) the page
  // hero already lists the pillars as cards — no need to double-render.
  if (pathname === "/brand-studio" || pathname === "/brand-studio/home") return null;

  const activeStageId = detectBrandStage(pathname);
  const missionAccent = BUILD_AND_PROTECT_MISSION.color;
  const isPhase1Canonical = PHASE1_CANONICAL_PATHS.includes(pathname);
  const activePillarId = detectBrandPillar(pathname);
  const pillarWorkflow = activePillarId ? BRAND_PILLAR_WORKFLOWS[activePillarId] : null;

  const pillars = BUILD_AND_PROTECT_MISSION.stages.map((s) => ({
    id: s.id,
    label: localized(s.label, language),
    subtitle: localized(s.subtitle, language),
    page: s.page,
    color: s.color || missionAccent,
    Icon: ICON_BY_NAME[s.icon] || Layers,
  }));

  // The active pillar's color drives Layer-2 children styling (cohesion).
  const activePillar = pillars.find((p) => p.id === activeStageId);
  const childAccent = activePillar?.color || missionAccent;

  // Phase-1 header reads "MISSION: BUILD & PROTECT THE BRAND".
  // Legacy header reads "Brand Studio: Pillars".
  const headerEyebrow = isPhase1Canonical ? t("Mission") : t("Brand Studio");
  const headerValue = isPhase1Canonical
    ? localized(BUILD_AND_PROTECT_MISSION.label, language)
    : t("Pillars");

  return (
    <div className="w-full mb-3" data-testid="brand-studio-pillars-breadcrumb">
      {/* ── Row 1: Mission + Pillars strip ───────────────────────── */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--app-text-muted)]">
          {headerEyebrow}:
        </span>
        <span
          className="text-[10px] uppercase tracking-widest font-bold"
          style={{ color: missionAccent }}
          data-testid="brand-studio-pillars-breadcrumb-mission-label"
        >
          {headerValue}
        </span>
      </div>
      <div className="flex items-center gap-1 flex-wrap" data-testid="brand-studio-pillars-breadcrumb-strip">
        {pillars.map((it, i) => {
          const isCurrent = it.id === activeStageId;
          const accent = it.color;
          // Per-pillar tints (paco 2026-06-11 PM-9): every pill carries its
          // own color family. Current = vivid; non-current = subtle tint.
          const pillStyle = isCurrent
            ? { borderColor: `${accent}88`, backgroundColor: `${accent}1f`, color: accent, boxShadow: `0 1px 0 ${accent}22, 0 0 0 1px ${accent}33` }
            : { borderColor: `${accent}33`, backgroundColor: `${accent}0d`, color: "var(--app-text)" };
          const numStyle = isCurrent
            ? { backgroundColor: `${accent}40`, color: accent }
            : { backgroundColor: `${accent}22`, color: `${accent}cc` };
          return (
            <div key={it.id} className="flex items-center shrink-0">
              <button
                onClick={() => navigate(it.page)}
                className="group flex items-center gap-2 rounded-xl px-3 py-2 border transition-all cursor-pointer hover:scale-[1.02] hover:-translate-y-px"
                style={pillStyle}
                data-testid={`pillar-${it.id}`}
                data-current={isCurrent || undefined}
              >
                {/* Number bubble — kept per paco's directive. */}
                <span
                  className="inline-flex w-5 h-5 items-center justify-center rounded-full text-[10px] font-bold shrink-0"
                  style={numStyle}
                >
                  {i + 1}
                </span>
                {/* Sprint 2026-06-11 PM-9 Phase 1D — icon circle. */}
                <span
                  className="inline-flex w-7 h-7 items-center justify-center rounded-full shrink-0 transition-transform group-hover:rotate-3"
                  style={{
                    backgroundColor: isCurrent ? `${accent}28` : `${accent}14`,
                    color: accent,
                    boxShadow: isCurrent ? `inset 0 0 0 1px ${accent}40` : `inset 0 0 0 1px ${accent}20`,
                  }}
                  data-testid={`pillar-icon-${it.id}`}
                >
                  <it.Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
                </span>
                {/* Label + subtitle stacked. */}
                <span className="flex flex-col items-start min-w-0 leading-tight pr-1">
                  <span className="text-[12px] font-semibold whitespace-nowrap">
                    {it.label}
                  </span>
                  <span
                    className="text-[10px] font-medium whitespace-nowrap opacity-75"
                    style={{ color: isCurrent ? `${accent}cc` : "var(--app-text-muted)" }}
                  >
                    {it.subtitle}
                  </span>
                </span>
                {/* Sprint 2026-06-11 PM-9 Phase 1C — pulse heartbeat. */}
                {counts.recent_24h?.pillars?.[it.id] && (
                  <PulseDot color={accent} testid={`pulse-pillar-${it.id}`} />
                )}
                {/* Sprint 2026-06-11 PM-9 Phase 1B — count badge.
                    Renders only when count > 0. For drawer-enabled pillars
                    (protect / strengthen / steward) it acts as a button
                    that toggles the popover. Brand Health renders a
                    non-interactive score badge (it's a single number). */}
                {(() => {
                  const cnt = counts.pillars?.[it.id];
                  if (cnt == null || cnt === 0) return null;
                  const drawable = DRAWER_PILLARS.has(it.id);
                  const badgeKey = `pillar:${it.id}`;
                  const badgeStyle = {
                    backgroundColor: isCurrent ? accent : `${accent}cc`,
                    color: "var(--app-bg)",
                  };
                  if (!drawable) {
                    // Brand Health → score-only, non-interactive.
                    return (
                      <span
                        className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full px-1.5 text-[10px] font-bold"
                        style={badgeStyle}
                        data-testid={`pillar-count-${it.id}`}
                        data-count={cnt}
                      >
                        {cnt}
                      </span>
                    );
                  }
                  return (
                    <span
                      role="button"
                      tabIndex={0}
                      ref={(el) => { badgeRefs.current[badgeKey] = el; }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDrawer(openDrawer === badgeKey ? null : badgeKey);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          setOpenDrawer(openDrawer === badgeKey ? null : badgeKey);
                        }
                      }}
                      className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full px-1.5 text-[10px] font-bold cursor-pointer hover:scale-110 transition-transform"
                      style={badgeStyle}
                      title={t("Click to see oldest at this stage")}
                      data-testid={`pillar-count-${it.id}`}
                      data-count={cnt}
                    >
                      {cnt}
                    </span>
                  );
                })()}
              </button>
              {openDrawer === `pillar:${it.id}` && DRAWER_PILLARS.has(it.id) && (
                <BrandStageDrawer
                  stage={it.id}
                  count={counts.pillars?.[it.id] || 0}
                  accent={accent}
                  anchorRef={{ current: badgeRefs.current[`pillar:${it.id}`] }}
                  onClose={() => setOpenDrawer(null)}
                />
              )}
              {i < pillars.length - 1 && (
                <ChevronRight className="w-3 h-3 text-[var(--app-text-muted)] opacity-40 shrink-0 mx-0.5" />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Row 2: Active pillar's children (Phase-1 canonical paths only) ──
           Sprint 2026-06-11 PM-9 Phase 1C (paco): Row 2 is indented to
           visually convey hierarchy (mission → active pillar → children).
           A subtle elbow connector (└) drawn via borders ties it back to
           Row 1, so the row reads like an indented outline. */}
      {isPhase1Canonical && pillarWorkflow && (
        <div
          className="mt-2 pl-7 relative"
          data-testid="brand-studio-pillar-children-breadcrumb"
        >
          {/* Elbow connector (└) — vertical stem + horizontal arm, drawn
              with absolute-positioned borders so it scales with the row. */}
          <span
            aria-hidden="true"
            className="absolute top-0 left-2 w-3 h-[18px] border-l border-b rounded-bl-md pointer-events-none"
            style={{ borderColor: `${childAccent}55` }}
          />
          <div className="flex items-center gap-1.5 mb-1.5">
            <span
              className="text-[10px] uppercase tracking-widest font-bold"
              style={{ color: childAccent }}
            >
              {localized(pillarWorkflow.label, language)}
            </span>
          </div>
          <div
            className="flex items-center gap-1 flex-wrap"
            data-testid="brand-studio-pillar-children-strip"
          >
            {pillarWorkflow.items.map((child, ci) => {
              const isCurrentChild = pathname === child.page;
              const childPillStyle = isCurrentChild
                ? { borderColor: `${childAccent}88`, backgroundColor: `${childAccent}1f`, color: childAccent, boxShadow: `0 1px 0 ${childAccent}22, 0 0 0 1px ${childAccent}33` }
                : { borderColor: `${childAccent}26`, backgroundColor: `${childAccent}08`, color: "var(--app-text)" };
              const childNumStyle = isCurrentChild
                ? { backgroundColor: `${childAccent}40`, color: childAccent }
                : { backgroundColor: `${childAccent}1a`, color: `${childAccent}bb` };
              return (
                <div key={child.id} className="flex items-center shrink-0">
                  <button
                    onClick={() => navigate(child.page)}
                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-medium border transition-all cursor-pointer hover:scale-[1.02]"
                    style={childPillStyle}
                    data-testid={`pillar-child-${child.id}`}
                    data-current={isCurrentChild || undefined}
                  >
                    <span
                      className="inline-flex w-4 h-4 items-center justify-center rounded-full text-[9px] font-bold shrink-0"
                      style={childNumStyle}
                    >
                      {ci + 1}
                    </span>
                    {localized(child.label, language)}
                    {/* Sprint 2026-06-11 PM-9 Phase 1C — pulse heartbeat. */}
                    {counts.recent_24h?.children?.[child.id] && (
                      <PulseDot color={childAccent} testid={`pulse-child-${child.id}`} />
                    )}
                    {/* Sprint 2026-06-11 PM-9 Phase 1B — child count badge. */}
                    {(() => {
                      const cnt = counts.children?.[child.id];
                      if (cnt == null || cnt === 0) return null;
                      const drawable = DRAWER_CHILDREN.has(child.id);
                      const badgeKey = `child:${child.id}`;
                      const badgeStyle = {
                        backgroundColor: isCurrentChild ? childAccent : `${childAccent}cc`,
                        color: "var(--app-bg)",
                      };
                      if (!drawable) {
                        return (
                          <span
                            className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[16px] rounded-full px-1 text-[10px] font-bold"
                            style={badgeStyle}
                            data-testid={`pillar-child-count-${child.id}`}
                            data-count={cnt}
                          >
                            {cnt}
                          </span>
                        );
                      }
                      return (
                        <span
                          role="button"
                          tabIndex={0}
                          ref={(el) => { badgeRefs.current[badgeKey] = el; }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDrawer(openDrawer === badgeKey ? null : badgeKey);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              setOpenDrawer(openDrawer === badgeKey ? null : badgeKey);
                            }
                          }}
                          className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[16px] rounded-full px-1 text-[10px] font-bold cursor-pointer hover:scale-110 transition-transform"
                          style={badgeStyle}
                          title={t("Click to see oldest at this stage")}
                          data-testid={`pillar-child-count-${child.id}`}
                          data-count={cnt}
                        >
                          {cnt}
                        </span>
                      );
                    })()}
                  </button>
                  {openDrawer === `child:${child.id}` && DRAWER_CHILDREN.has(child.id) && (
                    <BrandStageDrawer
                      stage={child.id}
                      count={counts.children?.[child.id] || 0}
                      accent={childAccent}
                      anchorRef={{ current: badgeRefs.current[`child:${child.id}`] }}
                      onClose={() => setOpenDrawer(null)}
                    />
                  )}
                  {ci < pillarWorkflow.items.length - 1 && (
                    <ChevronRight className="w-3 h-3 text-[var(--app-text-muted)] opacity-40 shrink-0 mx-0.5" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
